import { ofetch } from 'ofetch';
import type { FetchOptions } from 'ofetch';
import type { User } from '../stores/authStore';
import { getApiBaseURL } from '../lib/platform';

const API_BASE_URL = getApiBaseURL();

/**
 * 内存中的 accessToken（不落地 localStorage）
 * 由 authStore 登录/刷新后调用 setAccessToken 注入
 */
let inMemoryAccessToken: string | null = null;
export function setAccessToken(token: string | null) {
  inMemoryAccessToken = token;
}
export function getAccessToken(): string | null {
  return inMemoryAccessToken;
}

/**
 * 递归将对象的 key 从 snake_case 转换为 camelCase
 * 用于前端接收响应时自动转换，作为后端 ResponseInterceptor 的安全网
 */
function toCamelCase(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map((item) => toCamelCase(item));
  if (typeof obj !== 'object' || obj instanceof Date || obj instanceof File || obj instanceof FormData) return obj;
  const source = obj as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = toCamelCase(source[key]);
  }
  return result;
}

/**
 * 统一设置请求头的辅助函数：兼容 Headers 实例与普通对象两种 headers 形态
 */
function setHeader(options: { headers?: unknown }, key: string, value: string) {
  const headers = options.headers;
  if (headers instanceof Headers) {
    headers.set(key, value);
  } else if (headers && typeof headers === 'object') {
    (headers as Record<string, string>)[key] = value;
  } else {
    options.headers = { [key]: value };
  }
}

/**
 * 跨标签页单飞。同源开几个标签页就有几份 JS 上下文，下面的 `refreshInflight` 互不相见，
 * 两边同时到期就会把同一个 refreshToken 发出去两次 —— 服务端按「令牌复用」吊销整条令牌族，
 * 表现是偶发被踢回登录页（见 docs/接口契约.md）。
 *
 * 只需要**串行**，不需要跨标签页共享结果：HttpOnly cookie 是整个源共享的，后拿到锁的那次
 * 发的已经是前一次轮换出来的新 cookie，链式轮换天然命中不了复用检测。
 * `navigator.locks` 仅在安全上下文可用（https 与 localhost），拿不到时退回进程内单飞。
 */
const REFRESH_LOCK_NAME = 'yuyue:auth-refresh';
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return task();
  return locks.request(REFRESH_LOCK_NAME, task);
}

/**
 * 刷新 accessToken 的单飞控制：并发 401 时只发一次 refresh 请求
 * 返回 null 表示 refresh 失败（调用方自行跳登录页）
 */
let refreshInflight: Promise<string | null> | null = null;
async function doRefresh(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      return await withRefreshLock(refreshOnce);
    } catch {
      return null;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

async function refreshOnce(): Promise<string | null> {
  try {
    // 直接用 fetch 避免经过自己的拦截器递归。
    // refreshToken 由浏览器从 HttpOnly Cookie 自动携带，前端不读也不传。
    const resp = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!resp.ok) return null;
    const json = (await resp.json()) as {
      data?: { accessToken?: string; user?: User };
    };
    const data = json?.data;
    if (!data?.accessToken) return null;
    inMemoryAccessToken = data.accessToken;
    // 将 user 同步回 authStore（通过回调注入，避免循环依赖）
    if (data.user && onRefreshedUserCallback) {
      try { onRefreshedUserCallback(data.user); } catch { /* ignore */ }
    }
    return data.accessToken as string;
  } catch {
    return null;
  }
}

// authStore 注入的 user 更新回调（避免循环依赖）
let onRefreshedUserCallback: ((user: User) => void) | null = null;
export function registerRefreshedUserCallback(cb: (user: User) => void) {
  onRefreshedUserCallback = cb;
}

// 暴露给 authStore 使用的 refresh 函数
export async function refreshAccessToken(): Promise<string | null> {
  return doRefresh();
}

// 创建 ofetch 实例
const apiClient = ofetch.create({
  baseURL: API_BASE_URL,
  // refreshToken 在 HttpOnly Cookie 里，必须随请求携带
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
  async onRequest({ options }) {
    // 添加认证 token（内存）
    if (inMemoryAccessToken) {
      setHeader(options, 'Authorization', `Bearer ${inMemoryAccessToken}`);
    }

    // FormData / Blob 等二进制 body：必须剔除全局的 'Content-Type: application/json'，
    // 交由浏览器自动生成带 boundary 的 multipart/form-data，
    // 否则后端 body-parser 会把 multipart 边界当 JSON 解析失败。
    const body = options.body;
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
      const headers = options.headers;
      if (headers instanceof Headers) {
        headers.delete('Content-Type');
        headers.delete('content-type');
      } else if (headers && typeof headers === 'object') {
        delete (headers as Record<string, string>)['Content-Type'];
        delete (headers as Record<string, string>)['content-type'];
      }
    }

    // CSRF 令牌：从 XSRF-TOKEN cookie 读出后回送到 X-XSRF-TOKEN 头
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift();
    };
    const csrfToken = getCookie('XSRF-TOKEN');
    if (csrfToken) {
      setHeader(options, 'X-XSRF-TOKEN', csrfToken);
    }

    // 请求体不做转换，由后端 RequestInterceptor 统一将 camelCase 转为 snake_case
  },
  async onResponse({ response }) {
    // 响应自动将 snake_case 转换为 camelCase（安全网）
    try {
      const json = response._data;
      if (json && typeof json === 'object') {
        response._data = toCamelCase(json) as typeof response._data;
      }
    } catch {
      // 转换失败时不影响正常流程
    }
  },
  /**
   * 只负责把错误体翻成中文 Error。「401 → 刷新 → 重放」不在这里，见下方 request()。
   */
  async onResponseError({ response }) {
    // 解析错误 body：优先从 response._data 读取（ofetch 已预解析），
    // 其次尝试 response.json()，都失败才 fallback 到通用中文提示
    let errorMessage = '';
    let errorBody: unknown = response._data;

    if (!errorBody || typeof errorBody !== 'object') {
      try {
        errorBody = await response.json();
      } catch {
        errorBody = null;
      }
    }

    if (errorBody && typeof errorBody === 'object') {
      const body = errorBody as Record<string, unknown>;
      const message = body.message;
      if (typeof message === 'string' && message.trim()) {
        errorMessage = message;
      } else if (Array.isArray(message) && message.length) {
        // 后端 ValidationPipe 的数组 message 已由 AllExceptionsFilter 去重拼成字符串，
        // 正常走不到这里；保留分支是给没经过那个过滤器的响应（反向代理、旧版本后端）兜底
        errorMessage = message.map(String).join('；');
      } else if (typeof body.error === 'string' && body.error.trim()) {
        errorMessage = body.error;
      } else if (body.errors) {
        if (Array.isArray(body.errors)) {
          errorMessage = body.errors.map(String).join('；');
        } else if (typeof body.errors === 'object') {
          errorMessage = Object.values(body.errors as Record<string, unknown>)
            .flat()
            .map(String)
            .join('；');
        }
      }
    }

    // fallback：根据状态码给出友好的中文提示
    if (!errorMessage) {
      const statusMap: Record<number, string> = {
        400: '请求参数错误',
        401: '未授权或登录已过期',
        403: '您没有操作权限',
        404: '请求的资源不存在',
        408: '请求超时，请重试',
        409: '操作冲突，请刷新后重试',
        422: '提交的数据验证失败',
        429: '请求过于频繁，请稍后再试',
        500: '服务器异常，请稍后重试',
        502: '网关异常',
        503: '服务暂不可用',
        504: '网关超时',
      };
      errorMessage = statusMap[response.status] || `请求失败 (${response.status})`;
    }

    // 创建错误对象，包含更多信息
    const apiError = Object.assign(new Error(errorMessage), {
      status: response.status,
      statusText: response.statusText,
      data: errorBody,
    });
    throw apiError;
  },
});

// authStore 注入的未授权回调
let onUnauthorizedCallback: (() => void) | null = null;
export function registerUnauthorizedCallback(cb: () => void) {
  onUnauthorizedCallback = cb;
}

/** 登录/刷新/登出端点自身的 401 不算「会话过期」，不该据此去刷新或清会话 */
const AUTH_PATH_PATTERN = /\/auth\/(login|refresh|logout)/;

function httpStatusOf(error: unknown): number | undefined {
  const ctx = error as { status?: number; response?: { status?: number } } | null;
  return ctx?.status ?? ctx?.response?.status;
}

/**
 * 命中 401 时静默刷新一次令牌：true 表示已换到新令牌，调用方可以重放原请求。
 *
 * 刷新失败就是会话终结：清掉内存令牌、通知 authStore，再把路由翻回登录页。
 * 走 doRefresh() 这一条单飞，所以同屏多个查询同时 401 也只会发出一条 /auth/refresh。
 */
async function recoverSession(path: string, error: unknown): Promise<boolean> {
  if (AUTH_PATH_PATTERN.test(path) || httpStatusOf(error) !== 401) return false;
  if (await doRefresh()) return true;
  inMemoryAccessToken = null;
  if (onUnauthorizedCallback) {
    try { onUnauthorizedCallback(); } catch { /* ignore */ }
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
  return false;
}

/**
 * 统一解包 API 响应数据
 *
 * 后端存在两种响应格式：
 * 1. 标准包装格式: { success: boolean, data?: T, message?: string }
 *    此格式仅用于包装，data 才是实际数据，需要解包
 * 2. 分页/业务格式: { data?: [...], total, pagination, ... }
 *    此格式包含分页等元数据，不能简单提取 data，否则丢失元数据
 *
 * 判断规则：仅当响应同时包含 success 和 data 字段，且不含分页元数据时才解包
 */
function unwrapResponse<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'success' in raw) {
    const obj = raw as Record<string, unknown>;
    // 业务失败（无论是否有 data 字段）：抛异常透出后端 message，避免丢失错误原因
    if (obj.success === false) {
      const err = Object.assign(
        new Error(typeof obj.message === 'string' && obj.message.trim() ? obj.message : '操作失败'),
        {
          data: obj.data ?? null,
          code: obj.code,
          // 「需确认」不是失败：后端用它把候选单一起带回来，调用方据此弹确认框而不是弹 toast
          needConfirm: obj.needConfirm,
          duplicates: obj.duplicates,
          isBusinessError: true,
        },
      );
      throw err;
    }
    // 业务成功且有 data 字段（非分页响应）→ 解包
    if ('data' in obj && !('pagination' in obj) && !('total' in obj)) {
      return obj.data as T;
    }
  }
  return raw as T;
}

/**
 * 请求**根本没拿到响应**时的中文兜底（服务没起、断网、DNS 失败、被浏览器同源策略挡下）。
 *
 * 这类失败不经过上面的 `onResponseError`——那个钩子只在有 HTTP 响应时才跑。ofetch 直接把浏览器的
 * `TypeError: Failed to fetch` 包成 FetchError 抛出，`.message` 长成
 * `[GET] "/api/orders?orderBy=…&pageSize=9999": <no response> Failed to fetch`，
 * 而全站页面清一色 `e instanceof Error ? e.message : '…'` 原样显示，于是这句英文带整串查询参数
 * 直接铺进红条（2026-10-09 核算页实测）。
 *
 * 判据是「没有 response、没有 status、也不是业务失败」：服务端写的中文一律带 status
 * （onResponseError 造的）或 isBusinessError（unwrapResponse 造的），不会被这里盖掉。
 * 原始串留在 `rawMessage`，控制台与上报还要看得懂到底连的是哪个请求。
 */
const NETWORK_FAILURE_MESSAGE = '网络连接失败，请检查网络后重试';

function withNetworkFallback(error: unknown): unknown {
  if (!(error instanceof Error)) return error;
  const ctx = error as {
    response?: unknown;
    status?: number;
    isBusinessError?: boolean;
    code?: string;
    rawMessage?: string;
  };
  if (ctx.response !== undefined || typeof ctx.status === 'number' || ctx.isBusinessError === true) {
    return error;
  }
  ctx.rawMessage = error.message;
  error.message = NETWORK_FAILURE_MESSAGE;
  // 让 utils/errorHandler.ts 把它归成 NETWORK 类（蓝色样式 + 「请检查网络后重试」的建议）
  ctx.code = 'NETWORK_ERROR';
  return error;
}

/**
 * 裸 fetch 的传输层失败同样收口。
 *
 * 文件出口（凭证图、导出的 .xlsx、备份快照）走的是原生 `fetch` 而不是 apiClient，
 * 也就没有 `onResponseError` 那层中文翻译，连不上时抛出的就是 `Failed to fetch`。
 */
async function fetchOrNetworkFallback(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    throw withNetworkFallback(error);
  }
}

/**
 * 发一次请求，401 时「刷新 + 重放」一次再把结果交出去。
 *
 * 重放必须写在这一层，不能写在 ofetch 的 `onResponseError` 钩子里：ofetch 对 4xx/5xx 的处理是
 * `await callHooks(ctx, options.onResponseError)` 之后**恒**执行 `return await onError(ctx)`
 * （见 node_modules/ofetch/dist/shared/ofetch.*.mjs），钩子的返回值整个丢弃、原 Promise 照抛。
 * 于是此前那段钩子代码把重放请求真发出去了（网络面板里看得到它 200），调用方拿到的却仍是 401——
 * 表现是 accessToken 一到期（登录后 15 分钟），每个查询的首个请求都在界面上报「未授权或登录已过期」，
 * 用户手动重试才好，而且看不出原因。重放最多一次，第二次仍 401 就把错误原样抛给调用方。
 */
async function requestOnce<T>(path: string, options: FetchOptions<'json'>): Promise<T> {
  try {
    return unwrapResponse<T>(await apiClient<unknown>(path, options));
  } catch (error) {
    if (!(await recoverSession(path, error))) throw error;
    // 不自己拼 Authorization：onRequest 会从内存里读到刚换出的新令牌
    return unwrapResponse<T>(await apiClient<unknown>(path, options));
  }
}

/** 唯一的出口：`requestOnce` 传上来的错误里，凡是没有响应可依据的（传输层失败）一律换成中文。 */
async function request<T>(path: string, options: FetchOptions<'json'>): Promise<T> {
  try {
    return await requestOnce<T>(path, options);
  } catch (error) {
    throw withNetworkFallback(error);
  }
}

export async function get<T>(path: string, config?: { params?: Record<string, unknown> }): Promise<T> {
  // query 不做大小写转换（后端 RequestInterceptor 只转请求体）：
  // 参数名按 camelCase 原样上线，且必须与后端 query DTO 字段名逐字一致，
  // 否则会被 ValidationPipe({ whitelist: true }) 静默剥离。
  return request<T>(path, { method: 'GET', params: config?.params });
}

export async function post<T>(path: string, data?: unknown, config?: FetchOptions<'json'>): Promise<T> {
  return request<T>(path, { method: 'POST', body: data, ...config });
}

export async function put<T>(path: string, data?: unknown, config?: FetchOptions<'json'>): Promise<T> {
  return request<T>(path, { method: 'PUT', body: data, ...config });
}

export async function patch<T>(path: string, data?: unknown, config?: FetchOptions<'json'>): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: data, ...config });
}

export async function del<T>(path: string, config?: FetchOptions<'json'>): Promise<T> {
  return request<T>(path, { method: 'DELETE', ...config });
}

// 文件上传
export async function upload<T>(path: string, file: File, config?: FetchOptions<'json'>): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  return request<T>(path, { method: 'POST', body: formData, ...config });
}

/**
 * 拉取受保护的静态文件（身份证照片、支付凭证），返回可直接喂给 `<img src>` / `<a href>` 的 object URL。
 *
 * 取代此前 `withUploadToken` 把 accessToken 拼进 `?token=` 的做法：查询串会写进后端访问日志、
 * 反向代理日志与浏览器历史，还会随 Referer 头外泄给第三方。改成带头取回 Blob 后本地引用，
 * 令牌不再出现在任何 URL 里，后端也因此得以移除 `?token=` 入口。
 *
 * 走裸 fetch 而非 apiClient：这些地址不在 `/api` 前缀下，且需要的是 Blob 而不是 JSON 解包。
 * 调用方负责在不再需要时 `URL.revokeObjectURL`（见 useProtectedUploadUrl）。
 */
export async function fetchProtectedObjectUrl(url: string): Promise<string> {
  const request = (token: string | null) =>
    fetchOrNetworkFallback(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

  let resp = await request(inMemoryAccessToken);
  if (resp.status === 401) {
    const newToken = await doRefresh();
    if (!newToken) throw new Error('未授权或登录已过期');
    resp = await request(newToken);
  }
  if (!resp.ok) throw new Error(`文件获取失败 (${resp.status})`);

  return URL.createObjectURL(await resp.blob());
}

/** 从 Content-Disposition 取服务端给的文件名：优先 RFC 5987 的 UTF-8 名，退回 ASCII 名。 */
function parseFilename(disposition: string): string | null {
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8.trim());
    } catch {
      return null; // 头里的名字不是合法百分号编码：让调用方用自己的兜底名
    }
  }
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? null;
}

/**
 * 带鉴权地取一个文件出口，401 时刷新令牌重发一次。
 *
 * 走裸 fetch 而不是 apiClient：后者的 onResponse 会把整个响应对象的键名 snake→camel
 * 改写一遍，而备份快照的键名就是数据库列名（恢复时逐列写回），改写即损坏文件。
 * 令牌同样只放在 Authorization 头里，不进 URL。
 *
 * query 序列化与 `get()` 保持一致（跳过 null/undefined，数组按同名多值发），
 * 导出的筛选参数和列表页发出去的那一条必须逐字相同，否则「导出与所见不一致」。
 */
async function requestAuthorizedFile(path: string, params?: Record<string, unknown>): Promise<Response> {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) value.forEach((item) => search.append(key, String(item)));
    else search.append(key, String(value));
  });
  const query = search.toString();
  const url = `${API_BASE_URL}${path}${query ? `?${query}` : ''}`;
  const request = (token: string | null) =>
    fetchOrNetworkFallback(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

  let resp = await request(inMemoryAccessToken);
  if (resp.status === 401) {
    const newToken = await doRefresh();
    if (!newToken) throw new Error('未授权或登录已过期');
    resp = await request(newToken);
  }
  if (!resp.ok) {
    // 错误体是 Nest 的 {message}：把后端中文提示（含 403 的会员文案）透出，
    // 只报「下载失败 (403)」用户不知道该做什么。
    let message = '';
    try {
      const body = (await resp.json()) as { message?: unknown };
      if (typeof body?.message === 'string') message = body.message;
    } catch {
      // 非 JSON 的错误响应体，落到状态码提示
    }
    throw new Error(message || `下载失败 (${resp.status})`);
  }
  return resp;
}

/** 取回文本类产物（备份快照这类），按原始字符返回，不做任何键名改写。 */
export async function fetchAuthorizedFile(path: string): Promise<{ body: string; filename: string | null }> {
  const resp = await requestAuthorizedFile(path);
  return {
    body: await resp.text(),
    filename: parseFilename(resp.headers.get('Content-Disposition') ?? ''),
  };
}

/**
 * 取回二进制产物（.xlsx 这类）。必须走 `arrayBuffer` 而不是 `text`：
 * ZIP 的字节经文本解码就毁了，Excel 只会报文件已损坏。
 *
 * 文件名不给出去：调用方拼的名字带客户前缀（`张三_订单列表_2026-10-09.xlsx`），
 * 服务端那个是通用名，覆盖了反而丢掉筛选者是谁这条线索。
 */
export async function fetchAuthorizedBinary(
  path: string,
  params?: Record<string, unknown>,
): Promise<Uint8Array> {
  const resp = await requestAuthorizedFile(path, params);
  return new Uint8Array(await resp.arrayBuffer());
}

